import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem("token") || localStorage.getItem("token"); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Request OTP for login
  const requestOTP = async (email) => {
    const response = await axios.post(`${API}/auth/request-otp`, { email });
    return response.data;
  };

  // Verify OTP and complete login
  const verifyOTP = async (email, otp) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
    const { token: newToken, user: userData } = response.data;
    // Use localStorage so new tabs/windows share the session (token expires at 23:59:59 IST anyway)
    localStorage.setItem("token", newToken);
    sessionStorage.removeItem("token");
    // Reset PWA install popup on every fresh login
    try { sessionStorage.removeItem("pwa_dismissed_this_session"); } catch {}
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Resend OTP
  const resendOTP = async (email) => {
    const response = await axios.post(`${API}/auth/resend-otp`, { email });
    return response.data;
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    requestOTP,
    verifyOTP,
    resendOTP,
    logout,
    refreshUser: fetchUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isAgent: user?.role === "agent"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
